// @flow
import { differenceInHours, format } from "date-fns"
import { map } from "ramda"
import type { Dispatch } from "redux"
import type Booking from "@kiwicom/frontend-app/scripts/containers/mutable/Booking"

import type { GetState } from "common/types/state"
import type { BookingCompletionFormValues, CompletionPassenger } from "../types"
import type { BookingCompletionPayload } from "../../../types/outbound"
import type { DateValue } from "mmb/components/DateField/DateField"
import { bookingDataSelector } from "mmb/scenes/AccountBooking/components/Booking/services/selector"
import * as bookingsApi from "mmb/services/bookings/bookingsApi"
import { getUTCDate } from "../helpers/helpers"
import { reloadBooking } from "mmb/scenes/AccountBooking/components/Booking/services"
import { RELOAD_SUCCESS } from "mmb/scenes/AccountBooking/components/Booking/services/consts"

const BOOKING_COMPLETION_ACTIONS = {
  UPDATE: "bookingCompletion/UPDATE",
  POLL: "bookingCompletion/POLL",
  POLL_FAILURE: "bookingCompletion/POLL_FAILURE",
  POLL_SUCCESS: "bookingCompletion/POLL_SUCCESS",
  SUBMIT: "bookingCompletion/SUBMIT",
  SUBMIT_REQUEST: "bookingCompletion/SUBMIT_REQUEST",
  SUBMIT_SUCCESS: "bookingCompletion/SUBMIT_SUCCESS",
  SUBMIT_FAILURE: "bookingCompletion/SUBMIT_FAILURE",
}

type BookingCompletionUpdateAction = {
  type: "bookingCompletion/UPDATE",
  payload: Array<CompletionPassenger>,
}

type BookingCompletionRequestAction = {
  type: "bookingCompletion/SUBMIT_REQUEST",
  payload: BookingCompletionPayload,
}

type BookingCompletionSuccessAction = {
  type: "bookingCompletion/SUBMIT_SUCCESS",
  payload: BookingCompletionPayload,
}

type Actions =
  | BookingCompletionUpdateAction
  | BookingCompletionSuccessAction
  | BookingCompletionRequestAction

const InitialState = {
  completionPassengers: [],
  pendingIds: [],
}

type State = {
  completionPassengers: Array<CompletionPassenger>,
  pendingIds: Array<string>,
}

const isCheckInDataUpdated = data => data && data.status !== "refresh_pending"

const toInt = (string: string): number => parseInt(string, 0)

const getIdFromKey = (payload: BookingCompletionPayload) => Object.keys(payload)[0]

export const updatePassengerInState = (
  { completionPassengers }: State,
  payload: BookingCompletionPayload,
) =>
  // $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
  map(passenger => {
    const updatedPassengerId = getIdFromKey(payload)
    if (passenger.id === toInt(updatedPassengerId)) {
      return {
        ...passenger,
        isCheckedIn: true,
        document: {
          code: payload[updatedPassengerId].document_number,
          expiration: payload[updatedPassengerId].document_expiry,
        },
      }
    }
    return passenger
  })(completionPassengers)

export default function bookingCompletionReducer(
  state: State = InitialState,
  action: Actions,
): State {
  if (!state.completionPassengers) return InitialState

  switch (action.type) {
    case BOOKING_COMPLETION_ACTIONS.UPDATE:
      return { ...state, completionPassengers: action.payload }
    case BOOKING_COMPLETION_ACTIONS.SUBMIT_REQUEST:
      return { ...state, pendingIds: [...state.pendingIds, getIdFromKey(action.payload)] }
    case BOOKING_COMPLETION_ACTIONS.SUBMIT_SUCCESS:
      return {
        ...state,
        completionPassengers: updatePassengerInState(state, action.payload),
      }
    case BOOKING_COMPLETION_ACTIONS.POLL_SUCCESS:
      if (!isCheckInDataUpdated(action.res)) return state
      return {
        ...state,
        // $FlowFixMe Invalid error, payload is confirmed by BookingCompletionSuccessAction
        pendingIds: state.pendingIds.filter(id => id !== action.paxId),
      }
    case RELOAD_SUCCESS:
    case BOOKING_COMPLETION_ACTIONS.SUBMIT_FAILURE:
    case BOOKING_COMPLETION_ACTIONS.POLL_FAILURE:
      return { ...state, pendingIds: [] }
    default:
      return state
  }
}

/**
 *  Used to keep showing the checkin section even when all users have checked in.
 *  It should not be removed until the page is refreshed
 */
export const showBookingCompletion = (state: any): boolean =>
  state.bookingCompletion.completionPassengers.length > 0

export const isBookingCompletionPending = (state: any): boolean =>
  state.bookingCompletion.pendingIds.length > 0

export const update = (payload: Array<CompletionPassenger>) => ({
  type: BOOKING_COMPLETION_ACTIONS.UPDATE,
  payload,
})

const dateValueToString = ({ noExpiry, day, month, year }: DateValue) =>
  noExpiry ? null : format(new Date(toInt(year), toInt(month) - 1, toInt(day)), "YYYY-MM-DD")

const pollCheckinData = (bid: number, paxId: string, retryCount: number) => async (
  dispatch: Dispatch<any>,
) => {
  const promise = bookingsApi.getUpdatedCheckIn(bid)
  await dispatch({
    type: BOOKING_COMPLETION_ACTIONS.POLL,
    paxId,
    promise,
  })

  promise.then(payload => {
    if (isCheckInDataUpdated(payload) || retryCount > 10) {
      dispatch(reloadBooking(bid))
    } else {
      setTimeout(() => dispatch(pollCheckinData(bid, paxId, retryCount + 1)), 1000)
    }
  })
}

export const submitForm = (
  paxId: number,
  { travelDocumentNumber, travelDocumentExpiry }: BookingCompletionFormValues,
) => async (dispatch: Dispatch<any>, getState: GetState) => {
  const booking: Booking = bookingDataSelector(getState())
  const bid = booking.id
  const lastDepartureUTC = booking.journey.getDeparture().getIn(["when", "utc"])

  const itemsToTrack = {
    bid,
    canChangeDocuments: booking.canChangeDocuments(),
    departureUtc: lastDepartureUTC,
    nowUtc: new Date().toUTCString(),
    hoursUntilDeparture: differenceInHours(getUTCDate(), lastDepartureUTC),
  }
  const paxIdString = paxId.toString()
  const payload: BookingCompletionPayload = {
    [paxIdString]: {
      document_number: travelDocumentNumber,
      document_expiry: dateValueToString(travelDocumentExpiry),
    },
  }

  const ok = await dispatch({
    type: BOOKING_COMPLETION_ACTIONS.SUBMIT,
    payload,
    promise: bookingsApi.submitPassengerChange(bid, payload),
    _track: {
      ...payload,
      ...itemsToTrack,
    },
  })

  if (ok) {
    dispatch(pollCheckinData(bid, paxIdString, 0))
  }
  return ok
}
